/**
 * Mesh edit mode commands, Blender-style keys and menus, declared as data.
 *
 * Each command has the shape of the shared shortcut registry's entries
 * (`{ id, label, category, context, keys, run }`), so moving onto
 * `useCommands` is a matter of passing `meshCommands(api)`; menus map onto
 * pie / popup menus the same way. Until then `ModelTab` dispatches with
 * [`matchMeshCommand`].
 */

import type { MeshMode, MeshOptions } from '../../state/mesh-edit.js';

/** What commands can do (implemented by the model store). */
export interface MeshApi {
  op(name: string, params?: Record<string, unknown>): void;
  tool(tool: string): void;
  mode(mode: MeshMode): void;
  options(patch: Partial<MeshOptions>): void;
  getOptions(): MeshOptions;
  menu(id: MeshMenuId): void;
  editMode(on: boolean): void;
  toggleEditMode(): void;
  frame(): void;
}

export interface MeshCommand {
  /** Registry id. */
  id: string;
  label: string;
  category: 'Mesh';
  context: 'model.mesh';
  /** Key chords, registry syntax: `Ctrl+Shift+B`, `Alt+Z`, `NumpadPlus`. */
  keys: string[];
  /**
   * The engine handles this key itself while the viewport has focus (it
   * needs the mouse or the edit mode); the editor only dispatches it when
   * focus is elsewhere.
   */
  engine?: boolean;
  run: (api: MeshApi) => void;
}

export type MeshMenuId = 'select' | 'similar' | 'mesh' | 'merge' | 'delete' | 'split' | 'separate' | 'edge' | 'face' | 'vertex' | 'normals' | 'cleanup' | 'symmetrize' | 'falloff';

export interface MeshMenuEntry {
  label: string;
  /** Command id run by this entry (its keys show as the shortcut). */
  command?: string;
  /** Inline action (when not a command). */
  run?: (api: MeshApi) => void;
  /** Opens another menu. */
  submenu?: MeshMenuId;
  separator?: boolean;
  heading?: boolean;
}

const op =
  (name: string, params?: Record<string, unknown>) =>
  (api: MeshApi): void =>
    api.op(name, params);
const tool =
  (name: string) =>
  (api: MeshApi): void =>
    api.tool(name);
const menu =
  (id: MeshMenuId) =>
  (api: MeshApi): void =>
    api.menu(id);

function c(id: string, label: string, keys: string[], run: (api: MeshApi) => void, engine = false): MeshCommand {
  return { id: `model.mesh.${id}`, label, category: 'Mesh', context: 'model.mesh', keys, run, ...(engine ? { engine } : {}) };
}

export const MESH_COMMANDS: readonly MeshCommand[] = [
  // ---- modes
  c('editMode', 'Toggle Edit Mode', ['Tab'], (a) => a.toggleEditMode(), true),
  c('exit', 'Leave Edit Mode', ['Escape'], (a) => a.editMode(false), true),
  c('mode.vertex', 'Vertex Select', ['1'], (a) => a.mode('vertex'), true),
  c('mode.edge', 'Edge Select', ['2'], (a) => a.mode('edge'), true),
  c('mode.face', 'Face Select', ['3'], (a) => a.mode('face'), true),
  // ---- select
  c('selectAll', 'Select All', ['A'], op('selectAll')),
  c('selectNone', 'Select None', ['Alt+A'], op('selectNone')),
  c('selectInvert', 'Invert Selection', ['Ctrl+I'], op('selectInvert')),
  c('selectMore', 'Select More', ['Ctrl+NumpadPlus', 'Ctrl+='], op('selectMore')),
  c('selectLess', 'Select Less', ['Ctrl+NumpadMinus', 'Ctrl+-'], op('selectLess')),
  c('selectLinked', 'Select Linked', ['Ctrl+L'], op('selectLinked')),
  c('selectLinkedPick', 'Select Linked Under Mouse', ['L'], () => undefined, true),
  c('selectSimilarMenu', 'Select Similar…', ['Shift+G'], menu('similar')),
  c('checkerDeselect', 'Checker Deselect', [], op('checkerDeselect')),
  c('selectNonManifold', 'Select Non-Manifold', ['Ctrl+Shift+Alt+M'], op('selectNonManifold')),
  // ---- transform / tools
  c('grab', 'Move (Grab)', ['G'], tool('grab'), true),
  c('extrude', 'Extrude', ['E'], op('extrude')),
  c('inset', 'Inset Faces', ['I'], op('inset')),
  c('loopCut', 'Loop Cut and Slide', ['Ctrl+R'], tool('loopCut')),
  c('knife', 'Knife', ['K'], tool('knife')),
  c('knifeThrough', 'Knife (Cut Through)', ['Shift+K'], (a) => {
    a.options({ knifeCutThrough: true });
    a.tool('knife');
  }),
  c('bevel', 'Bevel Edges', ['Ctrl+B'], tool('bevel')),
  c('bevelVertices', 'Bevel Vertices', ['Ctrl+Shift+B'], tool('bevelVertices')),
  c('bisect', 'Bisect', [], tool('bisect')),
  c('edgeSlide', 'Edge Slide', [], tool('edgeSlide')),
  c('vertexSlide', 'Vertex Slide', ['Shift+V'], tool('vertexSlide')),
  c('subdivide', 'Subdivide', [], op('subdivide')),
  c('poke', 'Poke Faces', [], op('poke')),
  c('triangulate', 'Triangulate Faces', ['Ctrl+T'], op('triangulate')),
  c('trisToQuads', 'Tris to Quads', ['Alt+J'], op('trisToQuads')),
  c('makeFace', 'Make Face', ['F'], op('makeFace')),
  c('fill', 'Fill', ['Alt+F'], op('fill')),
  c('gridFill', 'Grid Fill', [], op('gridFill')),
  c('bridge', 'Bridge Edge Loops', [], op('bridge')),
  c('duplicate', 'Duplicate', ['Shift+D'], (a) => {
    a.op('duplicate');
    a.tool('grab');
  }),
  c('rip', 'Rip', ['V'], op('rip')),
  c('split', 'Split', ['Y'], op('split')),
  c('separateMenu', 'Separate…', ['P'], menu('separate')),
  c('splitMenu', 'Split…', ['Alt+M'], menu('split')),
  c('crease', 'Toggle Crease', ['Shift+E'], op('crease')),
  c('markSharp', 'Mark Sharp', [], op('markSharp')),
  c('clearSharp', 'Clear Sharp', [], op('markSharp', { clear: true })),
  c('markSeam', 'Mark Seam', [], op('markSeam')),
  c('clearSeam', 'Clear Seam', [], op('markSeam', { clear: true })),
  // ---- merge / delete
  c('mergeMenu', 'Merge…', ['M'], menu('merge')),
  c('mergeCenter', 'Merge at Center', [], op('merge', { at: 'center' })),
  c('mergeCursor', 'Merge at Cursor (origin)', [], op('merge', { at: 'cursor', point: [0, 0, 0] })),
  c('mergeCollapse', 'Collapse', [], op('merge', { at: 'collapse' })),
  c('mergeFirst', 'Merge at First', [], op('merge', { at: 'first' })),
  c('mergeLast', 'Merge at Last', [], op('merge', { at: 'last' })),
  c('mergeByDistance', 'Merge by Distance', [], op('mergeByDistance', { distance: 0.0001 })),
  c('deleteMenu', 'Delete…', ['X'], menu('delete')),
  c('delete', 'Delete', ['Delete', 'Backspace'], op('delete')),
  c('dissolve', 'Dissolve', ['Ctrl+X', 'Ctrl+Delete'], op('dissolve')),
  c('dissolveVerts', 'Dissolve Vertices', [], op('dissolveVerts')),
  c('dissolveEdges', 'Dissolve Edges', [], op('dissolveEdges')),
  c('dissolveFaces', 'Dissolve Faces', [], op('dissolveFaces')),
  c('limitedDissolve', 'Limited Dissolve', [], op('limitedDissolve')),
  c('deleteLoose', 'Delete Loose', [], op('deleteLoose')),
  c('deleteEdgeLoops', 'Delete Edge Loops', [], op('delete', { what: 'edgeLoops' })),
  // ---- normals / symmetry
  c('recalcOutside', 'Recalculate Outside', ['Shift+N'], op('recalcNormals')),
  c('recalcInside', 'Recalculate Inside', ['Ctrl+Shift+N'], op('recalcNormals', { inside: true })),
  c('flip', 'Flip Normals', [], op('flip')),
  c('normalsMenu', 'Normals…', ['Alt+N'], menu('normals')),
  c('symmetrizeMenu', 'Symmetrize…', [], menu('symmetrize')),
  // ---- menus
  c('edgeMenu', 'Edge Menu', ['Ctrl+E'], menu('edge')),
  c('faceMenu', 'Face Menu', ['Ctrl+F'], menu('face')),
  c('vertexMenu', 'Vertex Menu', ['Ctrl+V'], menu('vertex')),
  // ---- options
  c('proportional', 'Proportional Editing', ['O'], (a) => a.options({ proportional: !a.getOptions().proportional })),
  c('falloffMenu', 'Proportional Falloff…', ['Shift+O'], menu('falloff')),
  c('connected', 'Proportional Connected Only', ['Alt+O'], (a) => a.options({ connected: !a.getOptions().connected })),
  c('snap', 'Snapping', ['Shift+Tab'], (a) => a.options({ snap: !a.getOptions().snap })),
  c('xray', 'Toggle X-Ray', ['Alt+Z'], (a) => a.options({ xray: !a.getOptions().xray })),
  c('quad', 'Toggle Quad View', ['Ctrl+Alt+Q'], (a) => a.options({ quad: !a.getOptions().quad })),
  c('frame', 'Frame Selected', ['NumpadDecimal'], (a) => a.frame()),
];

export const MESH_MENUS: Record<MeshMenuId, { title: string; items: MeshMenuEntry[] }> = {
  select: {
    title: 'Select',
    items: [
      { label: 'All', command: 'model.mesh.selectAll' },
      { label: 'None', command: 'model.mesh.selectNone' },
      { label: 'Invert', command: 'model.mesh.selectInvert' },
      { separator: true, label: '' },
      { label: 'More', command: 'model.mesh.selectMore' },
      { label: 'Less', command: 'model.mesh.selectLess' },
      { label: 'Linked', command: 'model.mesh.selectLinked' },
      { label: 'Linked (delimit seams)', run: op('selectLinked', { delimitSeams: true }) },
      { label: 'Similar', submenu: 'similar' },
      { label: 'Checker Deselect', command: 'model.mesh.checkerDeselect' },
      { label: 'Non-Manifold', command: 'model.mesh.selectNonManifold' },
    ],
  },
  similar: {
    title: 'Select Similar',
    items: [
      { label: 'Normal / Direction', run: op('selectSimilar', { kind: 'normal', threshold: 0.1 }) },
      { label: 'Length / Area', run: op('selectSimilar', { kind: 'size', threshold: 0.05 }) },
      { label: 'Number of Sides / Edges', run: op('selectSimilar', { kind: 'sides' }) },
      { label: 'Perimeter (faces)', run: op('selectSimilar', { kind: 'perimeter', threshold: 0.05 }) },
      { label: 'Coplanar (faces)', run: op('selectSimilar', { kind: 'coplanar', threshold: 0.05 }) },
      { label: 'Face Angles (edges)', run: op('selectSimilar', { kind: 'faceAngle', threshold: 0.1 }) },
      { label: 'Crease / Sharp / Seam (edges)', run: op('selectSimilar', { kind: 'marks' }) },
    ],
  },
  mesh: {
    title: 'Mesh',
    items: [
      { label: 'Merge', submenu: 'merge' },
      { label: 'Split', submenu: 'split' },
      { label: 'Separate', submenu: 'separate' },
      { label: 'Delete', submenu: 'delete' },
      { label: 'Clean Up', submenu: 'cleanup' },
      { label: 'Symmetrize', submenu: 'symmetrize' },
      { label: 'Normals', submenu: 'normals' },
      { separator: true, label: '' },
      { label: 'Duplicate', command: 'model.mesh.duplicate' },
      { label: 'Bisect', command: 'model.mesh.bisect' },
      { label: 'Knife', command: 'model.mesh.knife' },
      { label: 'Rip', command: 'model.mesh.rip' },
    ],
  },
  merge: {
    title: 'Merge',
    items: [
      { label: 'At Center', command: 'model.mesh.mergeCenter' },
      { label: 'At Cursor', command: 'model.mesh.mergeCursor' },
      { label: 'Collapse', command: 'model.mesh.mergeCollapse' },
      { label: 'At First', command: 'model.mesh.mergeFirst' },
      { label: 'At Last', command: 'model.mesh.mergeLast' },
      { separator: true, label: '' },
      { label: 'By Distance', command: 'model.mesh.mergeByDistance' },
    ],
  },
  delete: {
    title: 'Delete',
    items: [
      { label: 'Vertices', run: op('delete', { what: 'vertices' }) },
      { label: 'Edges', run: op('delete', { what: 'edges' }) },
      { label: 'Faces', run: op('delete', { what: 'faces' }) },
      { separator: true, label: '' },
      { label: 'Dissolve Vertices', command: 'model.mesh.dissolveVerts' },
      { label: 'Dissolve Edges', command: 'model.mesh.dissolveEdges' },
      { label: 'Dissolve Faces', command: 'model.mesh.dissolveFaces' },
      { label: 'Limited Dissolve', command: 'model.mesh.limitedDissolve' },
      { label: 'Edge Loops', command: 'model.mesh.deleteEdgeLoops' },
    ],
  },
  split: {
    title: 'Split',
    items: [
      { label: 'Selection (Y)', command: 'model.mesh.split' },
      { label: 'Rip (V)', command: 'model.mesh.rip' },
    ],
  },
  separate: {
    title: 'Separate',
    items: [
      { label: 'Selection', run: op('separate') },
      { label: 'By Loose Parts', run: op('separate', { by: 'loose' }) },
    ],
  },
  edge: {
    title: 'Edge',
    items: [
      { label: 'Loop Cut and Slide', command: 'model.mesh.loopCut' },
      { label: 'Bevel Edges', command: 'model.mesh.bevel' },
      { label: 'Bridge Edge Loops', command: 'model.mesh.bridge' },
      { label: 'Subdivide', command: 'model.mesh.subdivide' },
      { label: 'Edge Slide', command: 'model.mesh.edgeSlide' },
      { separator: true, label: '' },
      { label: 'Mark Seam', command: 'model.mesh.markSeam' },
      { label: 'Clear Seam', command: 'model.mesh.clearSeam' },
      { label: 'Mark Sharp', command: 'model.mesh.markSharp' },
      { label: 'Clear Sharp', command: 'model.mesh.clearSharp' },
      { label: 'Toggle Crease', command: 'model.mesh.crease' },
    ],
  },
  face: {
    title: 'Face',
    items: [
      { label: 'Extrude', command: 'model.mesh.extrude' },
      { label: 'Inset', command: 'model.mesh.inset' },
      { label: 'Poke', command: 'model.mesh.poke' },
      { label: 'Make Face', command: 'model.mesh.makeFace' },
      { label: 'Fill', command: 'model.mesh.fill' },
      { label: 'Grid Fill', command: 'model.mesh.gridFill' },
      { label: 'Triangulate', command: 'model.mesh.triangulate' },
      { label: 'Tris to Quads', command: 'model.mesh.trisToQuads' },
      { label: 'Subdivide', command: 'model.mesh.subdivide' },
      { separator: true, label: '' },
      { label: 'Flip Normals', command: 'model.mesh.flip' },
    ],
  },
  vertex: {
    title: 'Vertex',
    items: [
      { label: 'Bevel Vertices', command: 'model.mesh.bevelVertices' },
      { label: 'Vertex Slide', command: 'model.mesh.vertexSlide' },
      { label: 'Make Face', command: 'model.mesh.makeFace' },
      { label: 'Rip', command: 'model.mesh.rip' },
      { label: 'Merge', submenu: 'merge' },
      { label: 'Dissolve Vertices', command: 'model.mesh.dissolveVerts' },
    ],
  },
  normals: {
    title: 'Normals',
    items: [
      { label: 'Flip', command: 'model.mesh.flip' },
      { label: 'Recalculate Outside', command: 'model.mesh.recalcOutside' },
      { label: 'Recalculate Inside', command: 'model.mesh.recalcInside' },
    ],
  },
  cleanup: {
    title: 'Clean Up',
    items: [
      { label: 'Delete Loose', command: 'model.mesh.deleteLoose' },
      { label: 'Limited Dissolve', command: 'model.mesh.limitedDissolve' },
      { label: 'Merge by Distance', command: 'model.mesh.mergeByDistance' },
      { label: 'Tris to Quads', command: 'model.mesh.trisToQuads' },
      { label: 'Select Non-Manifold', command: 'model.mesh.selectNonManifold' },
    ],
  },
  symmetrize: {
    title: 'Symmetrize',
    items: [
      { label: '+X to -X', run: op('symmetrize', { axis: 0 }) },
      { label: '-X to +X', run: op('symmetrize', { axis: 0, negative: true }) },
      { label: '+Y to -Y', run: op('symmetrize', { axis: 1 }) },
      { label: '-Y to +Y', run: op('symmetrize', { axis: 1, negative: true }) },
      { label: '+Z to -Z', run: op('symmetrize', { axis: 2 }) },
      { label: '-Z to +Z', run: op('symmetrize', { axis: 2, negative: true }) },
    ],
  },
  falloff: {
    title: 'Proportional Falloff',
    items: (['smooth', 'sphere', 'root', 'inverseSquare', 'sharp', 'linear', 'constant', 'random'] as const).map((f) => ({
      label: f === 'inverseSquare' ? 'Inverse Square' : f.charAt(0).toUpperCase() + f.slice(1),
      run: (a: MeshApi) => a.options({ falloff: f, proportional: true }),
    })),
  },
};

const CODE_NAMES: Record<string, string> = {
  Equal: '=',
  Minus: '-',
  NumpadAdd: 'NumpadPlus',
  NumpadSubtract: 'NumpadMinus',
  NumpadDecimal: 'NumpadDecimal',
  Delete: 'Delete',
  Backspace: 'Backspace',
  Tab: 'Tab',
  Escape: 'Escape',
  Enter: 'Enter',
  Space: 'Space',
};

/** Registry-style chord of a key event (`Ctrl+Shift+B`), from the physical key. */
export function chordOf(e: Pick<KeyboardEvent, 'code' | 'ctrlKey' | 'metaKey' | 'shiftKey' | 'altKey'>): string {
  const code = e.code;
  const key = code.startsWith('Key')
    ? code.slice(3)
    : code.startsWith('Digit')
      ? code.slice(5)
      : (CODE_NAMES[code] ?? code);
  const mods = [e.ctrlKey || e.metaKey ? 'Ctrl' : '', e.shiftKey ? 'Shift' : '', e.altKey ? 'Alt' : ''].filter(Boolean);
  return [...mods, key].join('+');
}

/** Normalizes a declared chord to the `chordOf` modifier order. */
export function normalizeChord(keys: string): string {
  const parts = keys.split('+');
  const key = parts.pop() ?? '';
  const has = (m: string) => parts.some((p) => p.toLowerCase() === m.toLowerCase());
  return [has('Ctrl') ? 'Ctrl' : '', has('Shift') ? 'Shift' : '', has('Alt') ? 'Alt' : '', key].filter(Boolean).join('+');
}

const BY_CHORD = new Map<string, MeshCommand>();
for (const cmd of MESH_COMMANDS) for (const k of cmd.keys) BY_CHORD.set(normalizeChord(k), cmd);
const BY_ID = new Map(MESH_COMMANDS.map((cmd) => [cmd.id, cmd]));

export function meshCommand(id: string): MeshCommand | undefined {
  return BY_ID.get(id);
}

/** The command bound to a key event, if any. */
export function matchMeshCommand(e: Pick<KeyboardEvent, 'code' | 'ctrlKey' | 'metaKey' | 'shiftKey' | 'altKey'>): MeshCommand | undefined {
  return BY_CHORD.get(chordOf(e));
}

/** Human shortcut for a command id (first chord), for tooltips and menus. */
export function shortcutOf(id: string): string | undefined {
  return BY_ID.get(id)?.keys[0];
}

/** Commands whose chords collide (should be none). */
export function keyConflicts(): string[] {
  const seen = new Map<string, string>();
  const out: string[] = [];
  for (const cmd of MESH_COMMANDS)
    for (const k of cmd.keys) {
      const n = normalizeChord(k);
      const prev = seen.get(n);
      if (prev && prev !== cmd.id) out.push(`${n}: ${prev} / ${cmd.id}`);
      seen.set(n, cmd.id);
    }
  return out;
}

/** Menu entries resolved for a context menu: label, shortcut and action. */
export function menuItems(id: MeshMenuId, api: MeshApi): Array<{ id: string; label: string; shortcut?: string; separator?: boolean; run?: () => void }> {
  return MESH_MENUS[id].items.map((entry, i) => {
    if (entry.separator) return { id: `sep${i}`, label: '', separator: true };
    if (entry.submenu) {
      const sub = entry.submenu;
      return { id: `sub-${sub}`, label: `${entry.label} ▸`, run: () => api.menu(sub) };
    }
    const cmd = entry.command ? BY_ID.get(entry.command) : undefined;
    return {
      id: entry.command ?? `${id}-${i}`,
      label: entry.label,
      ...(cmd?.keys[0] ? { shortcut: cmd.keys[0] } : {}),
      run: () => (cmd ? cmd.run(api) : entry.run?.(api)),
    };
  });
}

/** Parameter fields of the Adjust Last Operation panel, per operation. */
export interface AdjustField {
  key: string;
  label: string;
  kind: 'number' | 'int' | 'bool' | 'axis';
  min?: number;
  max?: number;
  step?: number;
}

export const ADJUST_FIELDS: Record<string, AdjustField[]> = {
  extrude: [{ key: 'amount', label: 'Distance', kind: 'number', step: 0.01 }],
  inset: [{ key: 'amount', label: 'Thickness', kind: 'number', min: 0, max: 0.99, step: 0.01 }],
  loopCut: [
    { key: 'cuts', label: 'Cuts', kind: 'int', min: 1, max: 64 },
    { key: 'slide', label: 'Slide', kind: 'number', min: -0.98, max: 0.98, step: 0.01 },
    { key: 'even', label: 'Even', kind: 'bool' },
    { key: 'flip', label: 'Flip', kind: 'bool' },
  ],
  bevel: [
    { key: 'offset', label: 'Width', kind: 'number', min: 0, step: 0.005 },
    { key: 'segments', label: 'Segments', kind: 'int', min: 1, max: 32 },
    { key: 'profile', label: 'Shape', kind: 'number', min: 0.05, max: 1, step: 0.05 },
    { key: 'clamp', label: 'Clamp Overlap', kind: 'bool' },
  ],
  bisect: [
    { key: 'fill', label: 'Fill', kind: 'bool' },
    { key: 'clearInner', label: 'Clear Inner', kind: 'bool' },
    { key: 'clearOuter', label: 'Clear Outer', kind: 'bool' },
  ],
  knife: [{ key: 'cutThrough', label: 'Cut Through', kind: 'bool' }],
  edgeSlide: [{ key: 'factor', label: 'Factor', kind: 'number', min: -1, max: 1, step: 0.01 }],
  vertexSlide: [{ key: 'amount', label: 'Factor', kind: 'number', min: 0, max: 1, step: 0.01 }],
  poke: [{ key: 'offset', label: 'Offset', kind: 'number', step: 0.01 }],
  triangulate: [{ key: 'beauty', label: 'Beauty', kind: 'bool' }],
  trisToQuads: [{ key: 'angle', label: 'Max Angle', kind: 'number', min: 0, max: 180, step: 1 }],
  mergeByDistance: [{ key: 'distance', label: 'Distance', kind: 'number', min: 0, step: 0.0001 }],
  limitedDissolve: [{ key: 'angle', label: 'Max Angle', kind: 'number', min: 0, max: 180, step: 1 }],
  gridFill: [
    { key: 'span', label: 'Span', kind: 'int', min: 0, max: 64 },
    { key: 'offset', label: 'Offset', kind: 'int', min: 0, max: 256 },
  ],
  bridge: [{ key: 'twist', label: 'Twist', kind: 'int', min: -64, max: 64 }],
  symmetrize: [
    { key: 'axis', label: 'Axis', kind: 'axis' },
    { key: 'negative', label: '-  to +', kind: 'bool' },
  ],
  dissolveEdges: [{ key: 'dissolveVerts', label: 'Dissolve Vertices', kind: 'bool' }],
  recalcNormals: [{ key: 'inside', label: 'Inside', kind: 'bool' }],
};

/** Display name of an operation (`loopCut` → `Loop Cut`). */
export function opTitle(name: string): string {
  return name.replace(/([A-Z])/g, ' $1').replace(/^./, (ch) => ch.toUpperCase());
}
